import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { IConfig } from '../../Interfaces/IConfig';
import { customFetcher } from '../../hooks/useFetch';
import { PublicLayout } from './PublicLayout';

interface IProps {
  configObject: IConfig;
}

type State = 'loading' | 'error';

interface IAcceptResetResponse {
  idContact?: number;
  TempSessionToken?: string;
}

/**
 * Landing-Seite für das Zurücksetzen des Passworts. Der Mandant gelangt über
 * den Link aus der Reset-E-Mail (SystemMail 22) hierher. Der Aufruf des
 * GET-Endpunkts prüft den Token (Eigentumsnachweis), setzt das Passwort
 * zurück und stößt direkt den 2FA-Code an (SystemMail 8) — KEINE zweite
 * E-Mail mit Auto-Login-Link mehr. Backend liefert idContact +
 * TempSessionToken zurück; wir legen das für den 2FA-Schritt in der
 * sessionStorage ab und leiten auf "/" weiter, wo der LoginDriver direkt die
 * 2FA-Code-Eingabe zeigt.
 */
export const ResetPasswordConfirm: React.FC<IProps> = ({ configObject }) => {
  const params = useParams<{ idContact: string; token: string }>();
  const [state, setState] = useState<State>('loading');
  const hasRequested = useRef(false);

  useEffect(() => {
    // Strict-Mode ruft Effekte doppelt auf; der Reset darf nur einmal
    // ausgelöst werden.
    if (hasRequested.current) {
      return;
    }
    hasRequested.current = true;

    if (!params.idContact || !params.token) {
      setState('error');
      return;
    }

    customFetcher({
      endpoint: `/contact/requestNewPassword/${params.idContact}/${params.token}`,
    })
      .then(res => {
        const data = res as IAcceptResetResponse;
        if (
          data?.idContact === undefined ||
          !data?.TempSessionToken
        ) {
          setState('error');
          return;
        }
        // 2FA-Handoff für den LoginDriver hinterlegen und auf "/" wechseln.
        sessionStorage.setItem(
          'pwReset2FA',
          JSON.stringify({
            idContact: data.idContact,
            token: data.TempSessionToken,
          })
        );
        window.location.href = '/';
      })
      .catch(() => setState('error'));
  }, [params.idContact, params.token]);

  return (
    <PublicLayout
      configObject={configObject}
      title='Passwort zurücksetzen'
      subtitle='Wir prüfen Ihren Link und melden Sie sicher an.'
    >
      {state === 'loading' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 4,
          }}
        >
          <CircularProgress />
          <Typography variant='body2' color='text.secondary'>
            Einen Moment – wir senden Ihnen einen Sicherheitscode und leiten Sie
            weiter …
          </Typography>
        </Box>
      )}

      {state === 'error' && (
        <Alert severity='warning'>
          <Typography variant='body2' sx={{ mb: 0.5 }}>
            <strong>Das hat leider nicht geklappt.</strong>
          </Typography>
          <Typography variant='body2'>
            Das Passwort kann nicht zurückgesetzt werden oder die Anfrage ist
            bereits abgelaufen. Bitte fordern Sie das Zurücksetzen erneut an. In
            dringenden Fällen kontaktieren Sie uns bitte.
          </Typography>
        </Alert>
      )}
    </PublicLayout>
  );
};
